import { useQuery } from '@tanstack/react-query';
import { RUNNER_PLATFORM_LABEL, NO_RUNNER_WAIT_MS, runnerPlatformOf, type Platform } from '@1qa/shared';
import { TriangleAlert } from '@/components/icons';
import { api } from '@/lib/api';

/**
 * Warns when no runner serves this project's platform (docs/design/mobile-runs.md): its runs would wait, then
 * fail. For mobile, also when the runners are connected but have no device ready (emulators still booting).
 */
export function RunnerWarning({ platform }: { platform: Platform }) {
  const status = useQuery({ queryKey: ['runner-status'], queryFn: api.runnerStatus, refetchInterval: 30_000, staleTime: 15_000 });
  const rp = runnerPlatformOf(platform);
  const counts = status.data?.known ? status.data.platforms[rp] : null;
  if (!counts) return null;
  const label = RUNNER_PLATFORM_LABEL[rp];
  // Only mobile runners need a device (emulator / phone); web, performance and security runners are ready once connected.
  const needsDevice = rp === 'android' || rp === 'ios';
  if (counts.runners > 0 && (!needsDevice || counts.devices > 0)) return null;
  // Android without emulators is fine when runners take tests on connected phones (Device Agents).
  if (rp === 'android' && status.data!.platforms.agent.runners > 0) return null;
  const noRunner = counts.runners === 0;
  return (
    <div role="status" className="flex items-start gap-3 rounded-card bg-warning-subtle p-4 text-sm text-fg ring-1 ring-warning/40 ring-inset">
      <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
      <div>
        <p className="font-semibold">{noRunner ? `No ${label} runner is connected` : `No ${label} device is ready yet`}</p>
        <p className="mt-0.5 text-fg-secondary">
          {noRunner
            ? `Runs of this project wait in the queue until one connects, and stop with an error after ${NO_RUNNER_WAIT_MS / 60_000} minutes. Ask an admin to start a runner for ${label} (RUNNER_PLATFORMS=${rp}).`
            : `The ${label} runner is connected, but its emulators are still starting or its devices are offline. Runs start as soon as a device is ready.`}
        </p>
      </div>
    </div>
  );
}
